/**
 * Was ein Kunde braucht, um den Bericht zu lesen (seit 08.10.2026).
 *
 * ANLASS: Robert las den Magnolia-Bericht als Yves: „verstehe ich alles, weiß
 * ich, was mit Fachprüfer gemeint ist, checke ich, was die ganzen
 * Simulationen sind und wieso sie gemacht wurden?" Nein. Der Bericht sagte
 * nicht, wogegen getestet wurde, nicht, dass neben dem Text-Test ein
 * Design-Check lief, und zwei Einträge mit derselben Adresse ließen offen,
 * welcher die neue Fassung war.
 *
 * Alles hier wird aus der eingefrorenen Nutzlast abgeleitet, auch für ältere
 * Berichte: Ablauf, Vorher/Nachher, welche Befunde zuerst stehen, der Stand.
 */

import { sprache, t } from './sprache';

/**
 * Grad nach Nielsen, in Worten für Kunden. Steht beim Fachprüfer. Seit
 * 09.10.2026 „verhindert die Anfrage“ statt „den Abschluss“: bei Finanzkunden
 * las sich „Abschluss“ als Jahresabschluss.
 */
export const GRAD_TEXT = '4 verhindert die Anfrage, 3 kostet spürbar, 2 stört, 0 bis 1 ist Feinschliff.';
export const GRAD_TEXT_EN = '4 prevents the inquiry, 3 costs noticeably, 2 is a nuisance, 0 to 1 is polish.';
/** GRAD_TEXT in der Sprache der Anzeige. */
export const gradText = () => t(GRAD_TEXT, GRAD_TEXT_EN);

export const NOTEN_TEXT = 'Die Personas scrollen die Seite am Desktop und am Handy durch und geben Noten von 0 bis 10; 5 ist neutral.';
export const NOTEN_TEXT_EN = 'The personas scroll through the page on desktop and on mobile and give scores from 0 to 10; 5 is neutral.';
export const notenText = () => t(NOTEN_TEXT, NOTEN_TEXT_EN);

export const FACHPRUEFER_TEXT = 'Ein zusätzlicher KI-Prüfer, kein Mensch. Er geht die Seite wie ein Designer nach festem Raster durch '
  + '(Hierarchie, Schrift, Abstände, Kontrast, Bedienung, Handy) und belegt jeden Befund mit dem Bildschirm, auf dem er ihn sieht.';
export const FACHPRUEFER_TEXT_EN = 'An additional AI reviewer, not a human. It goes through the page like a designer, using a fixed grid '
  + '(hierarchy, type, spacing, contrast, usability, mobile), and backs each finding with the screen where it sees it.';
export const fachprueferText = () => t(FACHPRUEFER_TEXT, FACHPRUEFER_TEXT_EN);

/**
 * Was Kernzielgruppe und erweiterte Zielgruppe heißen. Seit 12.10.2026 statt
 * „Kern“ und „Rand“: ein Leser des Magnolia-Berichts verstand „13 Personas (8
 * Kern, 5 Rand)“ nicht; am selben Tag wurde aus „Randleser“ die „erweiterte
 * Zielgruppe“ (Robert). Steht oben in Schritt 2 „Zielgruppe recherchiert“
 * (dort in zwei Kästen) und unter „Methode und Umfang“. Definition wie in
 * supabase/functions/_shared/sim/kern.ts.
 */
export const KERN_RAND_TEXT = 'Zur Kernzielgruppe gehören die Personas, für die die Seite gemacht ist. Ihr Urteil entscheidet. '
  + 'Die erweiterte Zielgruppe sind angrenzende Rollen, die mitlesen. Sie zählen nicht ins Urteil.';
export const KERN_RAND_TEXT_EN = 'The core audience is the personas the page is made for. Their verdict decides. '
  + 'The extended audience is neighboring roles who read along. They do not count toward the verdict.';
export const kernRandText = () => t(KERN_RAND_TEXT, KERN_RAND_TEXT_EN);

/** Der späteste Zeitpunkt, an dem dem Bericht etwas hinzugefügt wurde. */
export function standDesBerichts(data: any): string {
  const zeiten = [data?.published_at, data?.generated_at, ...(Array.isArray(data?.design) ? data.design.map((d: any) => d?.am) : [])]
    .map((z) => String(z ?? ''))
    .filter((z) => !Number.isNaN(new Date(z).getTime()));
  return zeiten.sort().pop() ?? '';
}

/** Wie `fassungHash` auf dem Server (skizze.ts): SHA-256 des getrimmten Texts, die ersten 16 Hex-Zeichen. */
export async function fassungHash(text: string): Promise<string> {
  const daten = new TextEncoder().encode(String(text ?? '').trim());
  const hash = await crypto.subtle.digest('SHA-256', daten);
  return Array.from(new Uint8Array(hash)).map((b) => b.toString(16).padStart(2, '0')).join('').slice(0, 16);
}

export interface FassungRef { nr: number; label: string }

/** Hash der Fassung → Nummer und Name im Bericht, damit eine Skizze sagt, welche Fassung sie zeigt. */
export async function fassungenNachHash(varianten: any[]): Promise<Record<string, FassungRef>> {
  const out: Record<string, FassungRef> = {};
  await Promise.all(varianten.map(async (v, i) => {
    if (typeof v?.text === 'string' && v.text.trim()) out[await fassungHash(v.text)] = { nr: i + 1, label: String(v.label ?? '') };
  }));
  return out;
}

export type DesignEintrag =
  | { art: 'paar'; heute: any; skizze: any }
  | { art: 'einzeln'; d: any };

/**
 * Live-Seite und Skizze derselben Adresse werden ein Paar (vorher, nachher).
 * Gibt es mehrere Skizzen, bekommt die jüngste die Live-Seite, die übrigen
 * stehen einzeln.
 */
export function designEintraege(design: any[]): DesignEintrag[] {
  const liste = Array.isArray(design) ? design : [];
  const heute = new Map<string, any>();
  for (const d of liste) if (!d?.skizze && d?.url && !heute.has(d.url)) heute.set(d.url, d);
  const vergeben = new Set<any>();
  const out: DesignEintrag[] = [];
  for (const d of liste) {
    if (vergeben.has(d)) continue;
    if (d?.skizze && heute.has(d.url) && !vergeben.has(heute.get(d.url))) {
      const h = heute.get(d.url);
      vergeben.add(h); vergeben.add(d);
      out.push({ art: 'paar', heute: h, skizze: d });
    }
  }
  for (const d of liste) if (!vergeben.has(d)) out.push({ art: 'einzeln', d });
  return out;
}

export const KRITERIEN = ['erster_eindruck', 'passt_zu_mir', 'glaubwuerdig', 'orientierung', 'handy'] as const;

export interface NotenZeile { kriterium: string; heute: number | null; neu: number | null; abstand: number | null }

const rund = (n: number) => Math.round(n * 10) / 10;

/** Je Kriterium heute gegen Skizze, dazu das Mittel des Abstands und wo er am größten ist. */
export function vergleicheNoten(heute: any, skizze: any): { zeilen: NotenZeile[]; mittel: number | null; deutlichste: NotenZeile | null } {
  const zeilen = KRITERIEN.map((k) => {
    const a = typeof heute?.noten?.[k] === 'number' ? heute.noten[k] : null;
    const b = typeof skizze?.noten?.[k] === 'number' ? skizze.noten[k] : null;
    return { kriterium: k, heute: a, neu: b, abstand: a != null && b != null ? rund(b - a) : null };
  }).filter((z) => z.heute != null || z.neu != null);
  const mitAbstand = zeilen.filter((z) => z.abstand != null);
  const mittel = mitAbstand.length ? rund(mitAbstand.reduce((s, z) => s + (z.abstand as number), 0) / mitAbstand.length) : null;
  const deutlichste = mitAbstand.length
    ? mitAbstand.reduce((x, y) => (Math.abs(y.abstand as number) > Math.abs(x.abstand as number) ? y : x))
    : null;
  return { zeilen, mittel, deutlichste };
}

/**
 * Was vom Fachprüfer sichtbar bleibt: Grad 3 und 4. Gibt es keine, die zwei
 * schwersten, damit der Prüfer nicht stumm wirkt. Der Rest klappt auf.
 */
export function teileBefunde(befunde: any[]): { zuerst: any[]; weitere: any[] } {
  const alle = Array.isArray(befunde) ? [...befunde].sort((a, b) => Number(b?.schwere) - Number(a?.schwere)) : [];
  const schwer = alle.filter((b) => Number(b?.schwere) >= 3);
  const zuerst = schwer.length ? schwer : alle.slice(0, 2);
  return { zuerst, weitere: alle.filter((b) => !zuerst.includes(b)) };
}

export interface Schritt { titel: string; was: string; warum: string; am?: string }

/**
 * Der Ablauf in Schritten, aus dem, was im Bericht steht: Zielgruppe,
 * Text-Vergleich, Design-Check der heutigen Seite, Skizze.
 */
export function ablauf(data: any, fassungen: Record<string, FassungRef> = {}): Schritt[] {
  const schritte: Schritt[] = [];
  const personas: any[] = Array.isArray(data?.set?.personas) ? data.set.personas : [];
  const varianten: any[] = Array.isArray(data?.variants) ? data.variants : [];
  const design: any[] = Array.isArray(data?.design) ? data.design : [];

  if (personas.length) {
    const belege = personas.reduce((s, p) => s + (Array.isArray(p?.evidence) ? p.evidence.length : 0), 0);
    const rand = personas.filter((p) => p?.rolle === 'rand').length;
    schritte.push({
      titel: t('Zielgruppe recherchiert', 'Audience researched'),
      was: t(
        `${personas.length} Personas${belege ? `, gestützt auf ${belege} ${belege === 1 ? 'Beleg' : 'Belege'} aus echten Quellen` : ''}`
          + (rand ? `, davon ${personas.length - rand} aus der Kernzielgruppe und ${rand} aus der erweiterten Zielgruppe` : '') + '.',
        `${personas.length} ${personas.length === 1 ? 'persona' : 'personas'}${belege ? `, backed by ${belege} real-world ${belege === 1 ? 'source' : 'sources'}` : ''}`
          + (rand ? `, of which ${personas.length - rand} from the core audience and ${rand} from the extended audience` : '') + '.',
      ),
      // Was Kernzielgruppe und erweiterte Zielgruppe sind, steht im selben Akkordeon als
      // eigene Angabe („Methode und Umfang“), nicht zweimal.
      warum: t('Die Personas stehen für die Leute, die die Seite tatsächlich besuchen.', 'The personas stand for the people who actually visit the page.'),
    });
  }

  const vergleiche = varianten.filter((v) => v?.mode !== 'feedback');
  if (vergleiche.length) {
    const gegen = typeof data?.narrative?.vergleichsfassung === 'string' && data.narrative.vergleichsfassung.trim()
      ? data.narrative.vergleichsfassung.trim()
      : vergleiche.every((v) => v?.baseline_source === 'generated')
        ? t('eine von 99takes erzeugte Vergleichsfassung', 'a baseline generated by 99takes') : t('eure Vergleichsfassung', 'your baseline');
    schritte.push({
      titel: vergleiche.length === 1 ? t('Text verglichen', 'Text compared') : t(`${vergleiche.length} Textfassungen verglichen`, `${vergleiche.length} text versions compared`),
      was: vergleiche.length === 1
        ? t(`Die Fassung trat gegen ${gegen} an.`, `The version went up against ${gegen}.`)
        : t(`Jede Fassung lief in einem eigenen Vergleich gegen ${gegen}.`, `Each version ran in its own comparison against ${gegen}.`),
      warum: t('Jede Persona liest beide Texte, ohne zu wissen, welcher neu ist, gibt Noten und sagt, welchen sie vorzieht.',
        'Each persona reads both texts without knowing which one is new, gives scores and says which one they prefer.'),
      am: vergleiche[0]?.created_at,
    });
  }

  // Im Bericht als Vorschlag (S3) stehen die beiden Checks heute und neu unter
  // design_vergleich, oft ohne Checks der Studie (design_checks: "keine"). Sie
  // zählen hier mit, sonst nennte „Methode und Umfang“ keinen Design-Check,
  // obwohl Beleg 2 und 3 auf zweien beruhen. Ein Check, der auch unter design
  // steht (gleiche Adresse und Zeit), zählt einmal.
  // Nur im Bericht als Vorschlag: Layout A zeigt design_vergleich nicht und bleibt, wie es war.
  const mitVorschlag = typeof data?.vorschlag?.titel === 'string' && data.vorschlag.titel.trim() !== '';
  const vergleich = mitVorschlag && data?.design_vergleich && typeof data.design_vergleich === 'object' ? data.design_vergleich : null;
  const schonDa = (d: any) => design.some((x) => x?.url === d?.url && x?.am === d?.am);
  const vHeute = vergleich?.heute && !schonDa(vergleich.heute) ? vergleich.heute : null;
  const vNeu = vergleich?.neu && !schonDa(vergleich.neu) ? vergleich.neu : null;

  const eintraege = designEintraege(design);
  const heute = [...(vHeute ? [vHeute] : []), ...eintraege.map((e) => (e.art === 'paar' ? e.heute : !e.d.skizze ? e.d : null)).filter(Boolean)];
  const skizzen = [...(vNeu?.skizze ? [vNeu] : []), ...eintraege.map((e) => (e.art === 'paar' ? e.skizze : e.d.skizze ? e.d : null)).filter(Boolean)];
  const mitPruefer = [...design, vHeute, vNeu].some((d) => d?.handwerk);
  if (heute.length) {
    schritte.push({
      titel: t('Heutige Seite geprüft', 'Current page checked'),
      was: t('Die Personas scrollten die Seite am Desktop und am Handy durch', 'The personas scrolled through the page on desktop and on mobile')
        + (mitPruefer ? t(', dazu der Fachprüfer.', ', plus the expert reviewer.') : '.'),
      // Was der Fachprüfer ist, steht im Kapitel „Wie die Seite wirkt“, nicht zweimal.
      warum: t('Der Text-Vergleich liest nur Text. Wie die Seite wirkt, prüft dieser Schritt.', 'The text comparison reads only text. This step checks how the page comes across.'),
      am: heute[0]?.am,
    });
  }
  if (skizzen.length) {
    const ref = fassungen[skizzen[0]?.skizze?.fassung_hash ?? ''];
    schritte.push({
      titel: t('Neue Fassung eingesetzt', 'New version inserted'),
      was: t(
        `${ref ? `F${ref.nr} (${ref.label})` : 'Eine neue Fassung'} wurde in die heutige Gestaltung eingesetzt und genauso geprüft.`,
        `${ref ? `F${ref.nr} (${ref.label})` : 'A new version'} was inserted into the current design and checked the same way.`,
      ),
      warum: t('So zeigt sich, wie der neue Text in der bestehenden Gestaltung wirkt, bevor jemand sie neu baut.',
        'This shows how the new text works in the existing design before anyone rebuilds it.'),
      am: skizzen[0]?.am,
    });
  }
  // Die neue Fassung aus dem Vergleich, wenn sie keine Skizze ist: ein Entwurf
  // (HTML oder Bilder) oder eine Seite unter eigener Adresse.
  if (vNeu && !vNeu.skizze) {
    const entwurf = vNeu.quelle === 'html' || vNeu.quelle === 'bilder';
    schritte.push({
      titel: entwurf ? t('Entwurf geprüft', 'Draft checked') : t('Neue Fassung geprüft', 'New version checked'),
      was: t(
        `Die Personas scrollten ${entwurf ? 'den Entwurf' : 'die neue Fassung'} am Desktop und am Handy durch`,
        `The personas scrolled through ${entwurf ? 'the draft' : 'the new version'} on desktop and on mobile`,
      ) + (vNeu.handwerk ? t(', dazu der Fachprüfer.', ', plus the expert reviewer.') : '.'),
      warum: t('So lässt sich die neue Fassung neben die heutige Seite stellen.', 'This puts the new version next to the current page.'),
      am: vNeu.am,
    });
  }
  return schritte;
}

/**
 * Der Umfang der ganzen Studie in einer Zeile (seit 09.10.2026, Feld `umfang`
 * aus report.ts): „Insgesamt 8 Testrunden, 234 Urteile, 3 Design-Checks.
 * Gezeigt wird die letzte Runde." Anlass Scopevisio: der Kopf sagte „1 Fassung,
 * 48 Bewertungen", obwohl die Arbeit viele Runden hatte. Ältere Berichte haben
 * das Feld nicht; dann leer.
 */
export function umfangZeile(data: any): string {
  const u = data?.umfang;
  const n = (x: unknown) => (typeof x === 'number' && Number.isInteger(x) && x >= 0 ? x : 0);
  const runden = n(u?.runden);
  if (!runden) return '';
  const urteile = n(u.urteile);
  const checks = n(u.design_checks);
  const en = sprache() === 'en';
  const teile = (en
    ? [
      `${runden} ${runden === 1 ? 'test round' : 'test rounds'}`,
      urteile ? `${urteile} ${urteile === 1 ? 'verdict' : 'verdicts'}` : '',
      checks ? `${checks} ${checks === 1 ? 'design check' : 'design checks'}` : '',
    ]
    : [
      `${runden} ${runden === 1 ? 'Testrunde' : 'Testrunden'}`,
      urteile ? `${urteile} ${urteile === 1 ? 'Urteil' : 'Urteile'}` : '',
      checks ? `${checks} ${checks === 1 ? 'Design-Check' : 'Design-Checks'}` : '',
    ]).filter(Boolean);
  const g0 = u.gezeigt ?? {};
  // Eine Runde, alles gezeigt: das sagt die Zeile „Grundlage“ schon.
  if (runden === 1 && n(g0.runden) >= 1 && n(g0.design_checks) >= checks) return '';
  const liste = teile.length > 1 ? `${teile.slice(0, -1).join(', ')} ${en ? 'and' : 'und'} ${teile.at(-1)}` : teile[0];
  const satz = [en ? `In total, ${liste}.` : `Insgesamt ${liste}.`];
  const g = u.gezeigt ?? {};
  const gr = n(g.runden);
  if (gr && gr < runden) {
    satz.push(en
      ? (g.nur_letzte === true ? 'The report shows the last round.' : gr === 1 ? 'The report shows one of them.' : `The report shows ${gr} of them.`)
      : (g.nur_letzte === true ? 'Gezeigt wird die letzte Runde.' : gr === 1 ? 'Gezeigt wird eine davon.' : `Gezeigt werden ${gr} davon.`));
  }
  const gc = n(g.design_checks);
  if (checks && gc < checks) {
    satz.push(en
      ? (gc === 0 ? 'The design checks are not in the report.' : `The report includes ${gc === 1 ? 'one' : gc} of the design checks.`)
      : (gc === 0 ? 'Die Design-Checks stehen nicht im Bericht.' : `Im Bericht ${gc === 1 ? 'steht einer' : `stehen ${gc}`} der Design-Checks.`));
  }
  return satz.join(' ');
}

export interface BefundZeile { bereich: string; ansicht: string; heute: any | null; neu: any | null }

const schwere = (b: any) => Number(b?.schwere) || 0;

/** Anteil gemeinsamer Wortanfänge (5 Buchstaben, ab 4 Zeichen) zweier Befunde, 0 bis 1. */
function gemeinsam(a: unknown, b: unknown): number {
  const woerter = (t: unknown) => new Set((String(t ?? '').toLowerCase().match(/[\p{L}\p{N}]{4,}/gu) ?? []).map((w) => w.slice(0, 5)));
  const x = woerter(a), y = woerter(b);
  if (!x.size || !y.size) return 0;
  let n = 0;
  for (const w of x) if (y.has(w)) n++;
  return n / Math.min(x.size, y.size);
}

/**
 * Befunde des Fachprüfers für heute und die Skizze nebeneinander, je Bereich
 * und Ansicht eine Zeile (seit 08.10.2026). Vorher standen beide als lange
 * Listen nebeneinander, und dass „Tabelle am Handy“ auf beiden Seiten auftaucht,
 * sah man nur beim genauen Lesen. Sichtbar sind Zeilen mit Grad 3 oder 4 auf
 * einer der beiden Seiten; gibt es keine, die zwei schwersten. Mehrere Befunde
 * im selben Bereich werden nach Schwere gepaart.
 */
export function vergleicheBefunde(heute: any[], neu: any[]): { zuerst: BefundZeile[]; weitere: BefundZeile[] } {
  const gruppen = new Map<string, { h: any[]; n: any[] }>();
  const nimm = (liste: any[], seite: 'h' | 'n') => {
    for (const b of Array.isArray(liste) ? liste : []) {
      const k = `${b?.bereich ?? ''}|${b?.ansicht ?? ''}`;
      if (!gruppen.has(k)) gruppen.set(k, { h: [], n: [] });
      gruppen.get(k)![seite].push(b);
    }
  };
  nimm(heute, 'h');
  nimm(neu, 'n');
  const zeilen: BefundZeile[] = [];
  for (const [k, g] of gruppen) {
    const [bereich, ansicht] = k.split('|');
    g.n.sort((a, b) => schwere(b) - schwere(a));
    const offen = [...g.h].sort((a, b) => schwere(b) - schwere(a));
    for (const n of g.n) {
      // Gepaart wird nach gemeinsamen Wörtern, nicht nach Reihenfolge: „Link
      // kaum lesbar“ und „Felder ohne Label“ sind beide Barrierefreiheit am
      // Desktop, aber nicht derselbe Befund.
      let best = -1, wert = 0;
      offen.forEach((h, i) => { const w = gemeinsam(h?.was, n?.was); if (w > wert) { wert = w; best = i; } });
      const h = best >= 0 ? offen.splice(best, 1)[0] : null;
      zeilen.push({ bereich: bereich!, ansicht: ansicht!, heute: h, neu: n });
    }
    for (const h of offen) zeilen.push({ bereich: bereich!, ansicht: ansicht!, heute: h, neu: null });
  }
  const max = (z: BefundZeile) => Math.max(schwere(z.heute), schwere(z.neu));
  zeilen.sort((a, b) => max(b) - max(a));
  const schwer = zeilen.filter((z) => max(z) >= 3);
  const zuerst = schwer.length ? schwer : zeilen.slice(0, 2);
  return { zuerst, weitere: zeilen.filter((z) => !zuerst.includes(z)) };
}

/** Ein Segment wie „enterprise-headless-cms“ ist ein Kürzel, kein Text für Kunden. */
export function lesbaresSegment(segment: unknown): string {
  const s = String(segment ?? '').trim();
  return /^[a-z0-9]+(?:[-_][a-z0-9]+)+$/.test(s) ? '' : s;
}
